/**
 * 仪器 TXT → 谱图 PNG（ECharts 离屏渲染）。
 * 与安心购/鉴别订单等页面共用：解析、抽稀、导出 dataURL。
 */
import echarts from "echarts";

export const SPECTRUM_TXT_MAX_POINTS = 7000;

const SPECTRUM_CHART_X_MIN = 2;
const SPECTRUM_CHART_X_MAX = 15.5;
const SPECTRUM_CHART_X_INTERVAL = 1.35;
const SPECTRUM_CHART_Y_MIN = -50;
const SPECTRUM_CHART_Y_MAX = 500;
const SPECTRUM_CHART_Y_INTERVAL = 50;
const SPECTRUM_CHART_EXPORT_W = 1636;
const SPECTRUM_CHART_EXPORT_H = 408;

function parseScientificFloat(s) {
  let t = String(s == null ? "" : s).trim();
  if (!t) return NaN;
  t = t.replace(/\s+/g, "");
  if (/^-?\d+,\d+([eE][+-]?\d+)?$/.test(t)) {
    t = t.replace(",", ".");
  }
  return parseFloat(t);
}

export function parseSpectrumTxt(text) {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const lines = raw.split(/\r?\n/);
  const points = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const tabParts = line.split("\t");
    let tStr;
    let yStr;
    if (tabParts.length >= 2) {
      tStr = tabParts[0].trim();
      yStr = tabParts[1].trim();
    } else {
      const sp = line.split(/[;\s|]+/).filter(Boolean);
      if (sp.length < 2) continue;
      tStr = sp[0];
      yStr = sp[1];
    }
    const t = parseScientificFloat(tStr);
    const y = parseScientificFloat(yStr);
    if (!Number.isFinite(t) || !Number.isFinite(y)) continue;
    points.push([t, y]);
  }
  return points;
}

export function downsampleSpectrumPairs(pairs, maxLen) {
  if (!pairs || pairs.length <= maxLen) return pairs || [];
  const t0 = pairs[0][0];
  const t1 = pairs[pairs.length - 1][0];
  const span = t1 - t0 || 1;
  const numBins = Math.min(maxLen, pairs.length);
  const binW = span / numBins;
  const best = new Array(numBins);
  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i];
    let b = Math.floor((p[0] - t0) / binW);
    if (b < 0) b = 0;
    if (b >= numBins) b = numBins - 1;
    const cur = best[b];
    if (!cur || p[1] > cur[1]) best[b] = p;
  }
  const out = [];
  for (let b = 0; b < numBins; b++) {
    if (best[b]) out.push(best[b]);
  }
  const first = pairs[0];
  const last = pairs[pairs.length - 1];
  if (!out.length) return pairs.slice(0, maxLen);
  if (out[0][0] !== first[0] || out[0][1] !== first[1]) out.unshift(first);
  const tail = out[out.length - 1];
  if (tail[0] !== last[0] || tail[1] !== last[1]) out.push(last);
  if (out.length > maxLen) {
    const step = Math.ceil(out.length / maxLen);
    const thin = [];
    for (let i = 0; i < out.length; i += step) thin.push(out[i]);
    const t2 = thin[thin.length - 1];
    if (!t2 || t2[0] !== last[0] || t2[1] !== last[1]) thin.push(last);
    return thin;
  }
  return out;
}

/**
 * @param {Array<[number, number]>} pairs
 * @returns {Promise<string>} data:image/png;base64,...
 */
export function renderTxtSpectrumChartToDataUrlAsync(pairs) {
  if (!pairs || pairs.length < 2) {
    return Promise.reject(new Error("数据点不足"));
  }

  const chartW = SPECTRUM_CHART_EXPORT_W;
  const chartH = SPECTRUM_CHART_EXPORT_H;
  const axisColor = "#424242";
  const lineColor = "#ff1744";
  const chartBg = "#ffffff";
  const axisFont = 9;
  const nameFont = 9;

  const wrap = document.createElement("div");
  wrap.style.cssText =
    "position:fixed;left:-12000px;top:0;width:" +
    chartW +
    "px;height:" +
    chartH +
    "px;visibility:hidden;pointer-events:none;";
  document.body.appendChild(wrap);
  const chart = echarts.init(wrap, null, { renderer: "canvas" });

  chart.setOption({
    animation: false,
    backgroundColor: chartBg,
    textStyle: { color: axisColor, fontSize: axisFont },
    grid: { left: 46, right: 12, top: 26, bottom: 42, containLabel: false },
    xAxis: {
      type: "value",
      name: "[Unit: min]",
      nameLocation: "middle",
      nameGap: 20,
      nameTextStyle: { color: axisColor, fontSize: nameFont, fontWeight: "normal" },
      min: SPECTRUM_CHART_X_MIN,
      max: SPECTRUM_CHART_X_MAX,
      interval: SPECTRUM_CHART_X_INTERVAL,
      scale: false,
      axisLine: { onZero: false, lineStyle: { color: axisColor } },
      axisTick: { show: true, length: 4, lineStyle: { color: axisColor } },
      minorTick: {
        show: true,
        splitNumber: 5,
        length: 4,
        lineStyle: { color: "#333333", width: 1, opacity: 0.92 },
      },
      axisLabel: {
        color: axisColor,
        fontSize: axisFont,
        margin: 10,
        fontWeight: "normal",
        formatter: function (v) {
          return Number(v).toFixed(2);
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "[pA]",
      nameLocation: "end",
      nameGap: 4,
      nameTextStyle: { color: axisColor, fontSize: nameFont, fontWeight: "normal" },
      min: SPECTRUM_CHART_Y_MIN,
      max: SPECTRUM_CHART_Y_MAX,
      interval: SPECTRUM_CHART_Y_INTERVAL,
      scale: false,
      axisLine: { lineStyle: { color: axisColor } },
      axisTick: { show: true, length: 4, lineStyle: { color: axisColor } },
      minorTick: {
        show: true,
        splitNumber: 5,
        length: 2,
        lineStyle: { color: axisColor, opacity: 0.65 },
      },
      axisLabel: {
        color: axisColor,
        fontSize: axisFont,
        margin: 6,
        fontWeight: "normal",
        formatter: function (v) {
          return Number(v).toFixed(3);
        },
      },
      splitLine: { show: false },
    },
    series: [
      {
        type: "line",
        encode: { x: 0, y: 1 },
        data: pairs,
        showSymbol: false,
        lineStyle: { width: 1.2, color: lineColor },
        itemStyle: { opacity: 0 },
        clip: true,
      },
    ],
  });

  return new Promise(function (resolve, reject) {
    window.setTimeout(function () {
      let url = "";
      try {
        chart.resize();
        url = chart.getDataURL({ type: "png", pixelRatio: 1, backgroundColor: chartBg });
        if (!url || url.length < 100) {
          reject(new Error("谱图导出失败，请重试"));
          return;
        }
        resolve(url);
      } catch (e) {
        reject(e || new Error("谱图导出异常"));
      } finally {
        try {
          chart.dispose();
        } catch (e2) {
          /* ignore */
        }
        if (wrap && wrap.parentNode) {
          wrap.parentNode.removeChild(wrap);
        }
      }
    }, 100);
  });
}



// WEBPACK FOOTER //
// ./src/util/spectrumTxtChart.js